/**
 * CoachPlaybook - Login Page
 * Version: 0.6.0
 * Description: Discord OAuth login page with feature overview and dark theme
 */

import React from 'react';
import { useAuth } from '../../utils/auth/AuthContext';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    background: '#1e293b',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    border: '1px solid #334155',
    textAlign: 'center'
  },
  logo: {
    fontSize: '48px',
    marginBottom: '8px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#f8fafc',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: '0 0 32px 0',
    lineHeight: '1.5'
  },
  discordBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    background: '#5865F2',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    transition: 'background 0.2s, transform 0.1s',
    marginBottom: '32px'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '0 0 24px 0'
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#334155'
  },
  dividerText: {
    fontSize: '12px',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600'
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px'
  },
  featureCard: {
    background: '#0f172a',
    borderRadius: '10px',
    padding: '16px 12px',
    border: '1px solid #1e293b',
    textAlign: 'left'
  },
  featureIcon: {
    fontSize: '20px',
    marginBottom: '6px'
  },
  featureName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#e2e8f0',
    margin: '0 0 4px 0'
  },
  featureDesc: {
    fontSize: '11px',
    color: '#64748b',
    margin: 0,
    lineHeight: '1.4'
  },
  premiumBadge: {
    display: 'inline-block',
    background: '#7c3aed',
    color: '#ffffff',
    fontSize: '9px',
    fontWeight: '700',
    padding: '2px 6px',
    borderRadius: '4px',
    marginLeft: '6px',
    verticalAlign: 'middle',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  footer: {
    marginTop: '24px',
    fontSize: '11px',
    color: '#475569'
  }
};

const features = [
  { icon: '\uD83C\uDFC8', name: 'Play Designer', desc: 'Draw plays for any sport with drag-and-drop', free: true },
  { icon: '\uD83D\uDCCB', name: 'Roster Manager', desc: 'Track players, positions, and jersey numbers', free: true },
  { icon: '\uD83D\uDCCA', name: 'Game Stats', desc: 'Record and analyze game statistics', free: true },
  { icon: '\uD83D\uDD0D', name: 'Scouting', desc: 'Scout opponents and track tendencies', free: true },
  { icon: '\u2601\uFE0F', name: 'Cloud Sync', desc: 'Sync playbooks across all devices', free: false },
  { icon: '\uD83D\uDCC8', name: 'Advanced Analytics', desc: 'Deep stat breakdowns and trends', free: false }
];

const LoginPage = () => {
  const { login } = useAuth();

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>{'\uD83C\uDFC8'}</div>
        <h1 style={styles.title}>CoachPlaybook</h1>
        <p style={styles.subtitle}>
          The all-in-one coaching platform. Design plays, manage rosters, track stats, and scout opponents — all from your phone.
        </p>

        <button
          style={styles.discordBtn}
          onClick={login}
          onMouseEnter={(e) => { e.target.style.background = '#4752c4'; }}
          onMouseLeave={(e) => { e.target.style.background = '#5865F2'; }}
        >
          <svg width="20" height="16" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309-0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3461C53.9056 44.6391 54.2778 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6116 50.9259 47.4931 49.0021 48.2228C48.8762 48.2707 48.8202 48.4172 48.8818 48.5383C49.9417 50.6035 51.1680 52.5700 52.5765 54.4350C52.6325 54.5139 52.7305 54.5477 52.8229 54.5195C58.6164 52.7249 64.5187 50.0174 70.6331 45.5576C70.6863 45.5182 70.7199 45.4562 70.7255 45.3914C72.1765 30.0791 68.2195 16.7757 60.1968 4.9795C60.1772 4.9429 60.1436 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2802 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7546 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z" fill="white"/>
          </svg>
          Sign in with Discord
        </button>

        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>Features</span>
          <div style={styles.dividerLine} />
        </div>

        <div style={styles.featuresGrid}>
          {features.map((f) => (
            <div key={f.name} style={styles.featureCard}>
              <div style={styles.featureIcon}>{f.icon}</div>
              <p style={styles.featureName}>
                {f.name}
                {!f.free && <span style={styles.premiumBadge}>Premium</span>}
              </p>
              <p style={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>

        <p style={styles.footer}>
          Free tier includes core features. Premium unlocks cloud sync and advanced analytics.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
